import { Decimal } from "decimal.js";
import store from "@/stores/store";

/**
 * Central number/amount/quantity formatting module.
 * All user-facing numeric rendering must go through these helpers so the
 * decimal separator and grouping follow the application locale (not the
 * browser locale, not a hardcoded format).
 */

export const DEFAULT_CURRENCY = "EUR";

const LOCALE_MAP = {
    fr: "fr-FR",
    en: "en-US",
};

/**
 * Resolve the Intl locale from the application session locale ("fr" / "en").
 */
export function getNumberLocale() {
    const raw = store.state.session?.active_locale || "fr";
    const short = String(raw).split(/[-_]/)[0].toLowerCase();
    return LOCALE_MAP[short] || LOCALE_MAP.fr;
}

/**
 * The decimal separator character for the current app locale.
 */
export function getDecimalSeparator(locale = null) {
    const resolved = locale || getNumberLocale();
    return resolved.startsWith("fr") ? "," : ".";
}

function toFiniteNumber(value) {
    if (value === null || value === undefined || value === "") {
        return null;
    }
    if (value instanceof Decimal) {
        return value.toNumber();
    }
    const num =
        typeof value === "number"
            ? value
            : Number(String(value).trim().replace(",", "."));
    return Number.isFinite(num) ? num : null;
}

/**
 * Coerce an API quantity (number or decimal string) to a finite number.
 * QuantityField serializes integers as strings ("12"); never use `+` on them.
 */
export function toQuantityNumber(value) {
    return toFiniteNumber(value) ?? 0;
}

/**
 * Add quantities without string concatenation.
 */
export function sumQuantities(...values) {
    return values.reduce((total, value) => total + toQuantityNumber(value), 0);
}

/**
 * Physical stock still free after reservations. Never negative
 * (same rule as catalog total_stock_left).
 */
export function availableStockQuantity(count, ...reservedValues) {
    return Math.max(0, toQuantityNumber(count) - sumQuantities(...reservedValues));
}

/**
 * Figures shown by StockCount: available quantity and reserved suffix.
 * Negative physical stock is shown as-is; it is never relabeled as reserved.
 */
export function stockCountDisplayParts(count, ...reservedValues) {
    const physical = toQuantityNumber(count);
    const reserved = sumQuantities(...reservedValues);
    if (physical < 0) {
        return {
            available: physical,
            reserved: reserved > 0 ? reserved : 0,
        };
    }
    return {
        available: Math.max(0, physical - reserved),
        reserved: reserved <= 0 ? 0 : Math.min(physical, reserved),
    };
}

/**
 * Normalize grouping spaces: Intl may emit narrow no-break spaces (U+202F);
 * the app historically renders regular no-break spaces (U+00A0).
 */
function normalizeSpaces(formatted) {
    return formatted.replace(/\u202f/g, "\u00a0");
}

/**
 * Format a number with the app locale.
 * @param {number|string|Decimal|null} value
 * @param {object} options
 * @param {number} [options.minDecimals] - Minimum fraction digits.
 * @param {number} [options.maxDecimals] - Maximum fraction digits.
 * @param {boolean} [options.useGrouping=true] - Thousands separator.
 * @returns {string} Formatted number, or "-" for null/undefined/empty.
 */
export function formatNumber(
    value,
    { minDecimals = 0, maxDecimals = 10, useGrouping = true } = {}
) {
    const num = toFiniteNumber(value);
    if (num === null) {
        return "-";
    }
    return normalizeSpaces(
        num.toLocaleString(getNumberLocale(), {
            useGrouping,
            minimumFractionDigits: minDecimals,
            maximumFractionDigits: Math.max(minDecimals, maxDecimals),
        })
    );
}

/**
 * Format a quantity (max 3 decimals by default, trailing zeros removed).
 * @param {number|string|Decimal|null} value
 * @param {object} options
 * @param {string} [options.unit] - Optional unit suffix (e.g. "kg").
 * @param {number} [options.maxDecimals=3]
 * @returns {string}
 */
export function formatQuantity(value, { unit = "", maxDecimals = 3 } = {}) {
    const num = toFiniteNumber(value);
    if (num === null) {
        return "-";
    }
    const formatted = formatNumber(num, { maxDecimals });
    return unit ? `${formatted}\u00a0${unit}` : formatted;
}

const CURRENCY_SYMBOLS = {
    EUR: "€",
    USD: "$",
    GBP: "£",
    CHF: "CHF",
};

/**
 * Currency symbol for a currency code (falls back to the code itself).
 */
export function getCurrencySymbol(currency = DEFAULT_CURRENCY) {
    return CURRENCY_SYMBOLS[currency] || currency;
}

/**
 * Format a monetary amount with the app locale and currency.
 * Integers are shown without decimals (legacy behavior), other values
 * with 2 decimals.
 * @param {number|string|Decimal|null} value
 * @param {object} options
 * @param {string} [options.currency=DEFAULT_CURRENCY]
 * @param {boolean} [options.forceDecimals=false] - Always show 2 decimals.
 * @returns {string}
 */
export function formatAmount(
    value,
    { currency = DEFAULT_CURRENCY, forceDecimals = false } = {}
) {
    const num = toFiniteNumber(value);
    const symbol = getCurrencySymbol(currency);
    if (num === null) {
        return `-\u00a0${symbol}`;
    }
    const isInteger = Number.isInteger(num);
    const decimals = isInteger && !forceDecimals ? 0 : 2;
    const formatted = normalizeSpaces(
        num.toLocaleString(getNumberLocale(), {
            style: "currency",
            currency,
            minimumFractionDigits: decimals,
            maximumFractionDigits: decimals,
        })
    );
    return formatted;
}

/**
 * Format a 0-1 ratio as a percentage with the app locale.
 */
export function formatPercent(value, { maxDecimals = 2 } = {}) {
    const num = toFiniteNumber(value);
    if (num === null) {
        return "-\u00a0%";
    }
    return `${formatNumber(num * 100, { maxDecimals })}\u00a0%`;
}

/**
 * Sanitize free-text input to a decimal string using "." as separator.
 * Accepts both comma and point; strips other characters.
 * @param {string|number|null} value
 * @param {object} options
 * @param {number} [options.maxDecimals=4]
 * @param {boolean} [options.allowNegative=false]
 * @returns {string}
 */
export function sanitizeDecimalInputString(
    value,
    { maxDecimals = 4, allowNegative = false } = {}
) {
    if (value === null || value === undefined) {
        return "";
    }
    let raw = String(value);
    const negative = allowNegative && raw.trim().startsWith("-");
    let normalized = raw.replace(/[^0-9.,]/g, "").replace(/,/g, ".");

    const dotIndex = normalized.indexOf(".");
    if (dotIndex !== -1) {
        const integerPart = normalized.slice(0, dotIndex).replace(/\./g, "");
        const decimalPart = normalized
            .slice(dotIndex + 1)
            .replace(/\./g, "")
            .slice(0, maxDecimals);
        normalized = `${integerPart}.${decimalPart}`;
    }
    return negative ? `-${normalized}` : normalized;
}

/**
 * Parse user decimal input (comma or point separator) into a Number.
 * @param {string|number|null} value
 * @param {object} options
 * @param {number} [options.maxDecimals=4]
 * @param {boolean} [options.allowNegative=false]
 * @returns {number|null} Parsed value, or null when empty/invalid.
 */
export function parseDecimalInput(
    value,
    { maxDecimals = 4, allowNegative = false } = {}
) {
    if (value === "" || value === null || value === undefined) {
        return null;
    }
    if (typeof value === "number") {
        return Number.isFinite(value) ? value : null;
    }
    const normalized = sanitizeDecimalInputString(value, {
        maxDecimals,
        allowNegative,
    });
    if (normalized === "" || normalized === "." || normalized === "-") {
        return null;
    }
    const parsed = Number.parseFloat(normalized);
    if (Number.isNaN(parsed)) {
        return null;
    }
    if (!allowNegative && parsed < 0) {
        return null;
    }
    return parsed;
}

/**
 * Format a numeric value for display inside a text input, using the app
 * locale decimal separator, without grouping, without trailing zeros.
 * @param {number|string|Decimal|null} value
 * @param {number} [maxDecimals=6]
 * @returns {string}
 */
export function formatDecimalForInput(value, maxDecimals = 6) {
    const num = toFiniteNumber(value);
    if (num === null) {
        return "";
    }
    let text;
    if (Number.isInteger(num)) {
        text = String(num);
    } else {
        text = num
            .toFixed(maxDecimals)
            .replace(/(\.\d*?)0+$/, "$1")
            .replace(/\.$/, "");
    }
    return text.replace(".", getDecimalSeparator());
}
